import React, { useEffect, useRef, useState } from 'react';

const STEP = 32;

const storedWidth = (key, min, initial) => {
  try {
    const v = Number(localStorage.getItem(key));
    return Number.isFinite(v) && v >= min ? v : initial;
  } catch {
    return initial;
  }
};

// The width of a card that one of its edges resizes (drag, arrow keys, Home /
// End; double-click resets), remembered per browser under `storageKey`.
// `max()` is the room the card has now; it is read on every change.
export function useResizableWidth({ storageKey, min, initial, max }) {
  const [width, setWidth] = useState(() => storedWidth(storageKey, min, initial));
  const maxRef = useRef(max);
  maxRef.current = max;
  const stopDrag = useRef(null);
  const room = () => Math.max(min, maxRef.current());
  const clampWidth = (w) => Math.round(Math.min(room(), Math.max(min, w)));
  const save = (w) => {
    const next = clampWidth(w);
    setWidth(next);
    try { localStorage.setItem(storageKey, String(next)); } catch { /* private mode: not remembered */ }
  };
  useEffect(() => () => stopDrag.current?.(), []);

  // edge 'left': dragging left widens the card (its right edge stays put);
  // edge 'right': dragging right widens it.
  const startDrag = (e, edge) => {
    e.preventDefault();
    const startX = e.clientX;
    const start = Math.min(width, room());
    const widthAt = (ev) => start + (edge === 'left' ? startX - ev.clientX : ev.clientX - startX);
    const move = (ev) => setWidth(clampWidth(widthAt(ev)));
    const up = (ev) => { save(widthAt(ev)); stopDrag.current?.(); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    stopDrag.current = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      stopDrag.current = null;
    };
  };
  const onKey = (e, edge) => {
    const wider = edge === 'left' ? 'ArrowLeft' : 'ArrowRight';
    const narrower = edge === 'left' ? 'ArrowRight' : 'ArrowLeft';
    const now = Math.min(width, room());
    const next = {
      [wider]: now + STEP, [narrower]: now - STEP, Home: min, End: room(),
    }[e.key];
    if (next == null) return;
    e.preventDefault();
    save(next);
  };
  return {
    width, min, room, startDrag, onKey, reset: () => save(initial),
  };
}

// The resize grip on a card's `edge`: a thin line on hover and keyboard focus.
// It sits outside the card's scrolling content, so give the card a wrapper.
export function ResizeHandle({ resize, edge = 'left', label = 'Resize the panel' }) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuemin={resize.min}
      aria-valuemax={resize.room()}
      aria-valuenow={Math.min(resize.width, resize.room())}
      tabIndex={0}
      title="Drag to resize. Double-click to reset."
      onMouseDown={(e) => resize.startDrag(e, edge)}
      onKeyDown={(e) => resize.onKey(e, edge)}
      onDoubleClick={resize.reset}
      className={`group absolute inset-y-0 ${edge === 'left' ? '-left-1' : '-right-1'} z-20 w-2 cursor-col-resize focus:outline-none`}
    >
      <span className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-transparent transition-colors group-hover:bg-brand-500/60 group-focus-visible:bg-brand-500" />
    </div>
  );
}
